import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DeliveryMapping, DeliveryTier } from "@aura/shared";
import { cx } from "styled-system/css";
import { ak } from "./admin-styles";
import { Banner, PageHead, Section } from "~/components/editorial";
import { api } from "~/lib/api";
import { useIsAdmin } from "~/lib/auth";
import { fmtInt, plural } from "~/lib/format";

const QUERY_KEY = ["delivery-mapping"];

// Repo -> product -> tier for the delivery report. Repos follow the name
// rules unless an admin sets an override here; tiers belong to products.
export function ProductMappingPage() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const canEdit = useIsAdmin();
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<{ data: DeliveryMapping }>("/delivery/mapping"),
  });
  const mapping = data?.data;

  const onDone = {
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ["delivery"] });
    },
    onError: (e: Error) => setError(e.message),
  };
  const setTier = useMutation({
    mutationFn: ({ key, tier }: { key: string; tier: DeliveryTier | null }) =>
      api.patch(`/delivery/products/${encodeURIComponent(key)}`, { tier }),
    ...onDone,
  });
  const setRepo = useMutation({
    mutationFn: ({
      id,
      productKey,
    }: {
      id: string;
      productKey: string | null;
    }) => api.patch(`/delivery/repos/${id}`, { productKey }),
    ...onDone,
  });

  const unmapped = useMemo(
    () => (mapping?.repos ?? []).filter((r) => r.productKey === "unmapped"),
    [mapping],
  );
  const repos = onlyUnmapped ? unmapped : (mapping?.repos ?? []);
  const nameOf = (key: string | null) =>
    mapping?.products.find((p) => p.key === key)?.name ?? "none";

  return (
    <div className={ak.page}>
      <PageHead
        back={{ to: "/settings", label: "Settings" }}
        kicker="Admin"
        title="Products"
        dek="Which product each repo belongs to, and each product's tier, for the delivery report."
      />
      {!canEdit && (
        <Banner tone="caution">Only Aura admins can change products and tiers.</Banner>
      )}
      {error && (
        <Banner tone="caution" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {isLoading || !mapping ? (
        <div className={ak.loading}>Loading…</div>
      ) : (
        <>
          <Section
            kicker="I. Tiers"
            title="Product tiers"
            dek="The PxE total on the delivery report leaves out Outside PxE. Products with no tier still count toward PxE."
          >
            <div className={ak.tableWrap}>
              <table className={ak.table}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Tier</th>
                    <th data-num>Repos</th>
                  </tr>
                </thead>
                <tbody>
                  {mapping.products.map((p) => (
                    <tr key={p.key}>
                      <td>{p.name}</td>
                      <td>
                        <select
                          className={ak.select}
                          disabled={!canEdit}
                          aria-label={`Tier for ${p.name}`}
                          value={p.tier ?? ""}
                          onChange={(e) =>
                            setTier.mutate({
                              key: p.key,
                              tier: (e.target.value ||
                                null) as DeliveryTier | null,
                            })
                          }
                        >
                          <option value="">No tier</option>
                          {mapping.tiers.map((t) => (
                            <option key={t.key} value={t.key}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td data-num className={ak.mono}>
                        {fmtInt(
                          mapping.repos.filter((r) => r.productKey === p.key)
                            .length,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section
            kicker="II. Repos"
            title="Repo to product"
            dek={`Auto follows the repo name (tops-*, aries-*, da5-* and so on). ${plural(unmapped.length, "repo")} ${unmapped.length === 1 ? "has" : "have"} no product.`}
            actions={
              <button
                type="button"
                className={cx(ak.btnSecondary, ak.btnSm)}
                aria-pressed={onlyUnmapped}
                onClick={() => setOnlyUnmapped((v) => !v)}
              >
                {onlyUnmapped ? "Show all repos" : "Show unmapped only"}
              </button>
            }
          >
            <div className={ak.tableWrap}>
              <table className={ak.table}>
                <thead>
                  <tr>
                    <th>Repo</th>
                    <th>Product</th>
                    <th>From</th>
                  </tr>
                </thead>
                <tbody>
                  {repos.map((r) => (
                    <tr key={r.id}>
                      <td className={ak.mono}>{r.fullName}</td>
                      <td>
                        <select
                          className={ak.select}
                          disabled={!canEdit}
                          aria-label={`Product for ${r.fullName}`}
                          value={r.override ?? ""}
                          onChange={(e) =>
                            setRepo.mutate({
                              id: r.id,
                              productKey: e.target.value || null,
                            })
                          }
                        >
                          <option value="">
                            Auto ({r.inferred ? nameOf(r.inferred) : "unmapped"}
                            )
                          </option>
                          {mapping.products.map((p) => (
                            <option key={p.key} value={p.key}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className={ak.muted}>
                        {r.source === "override"
                          ? "Set here"
                          : r.source === "name"
                            ? "Repo name"
                            : "Unmapped"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
